import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check } from '../../icons';
import {
  getCompanyQuota, setMemberDefault, setMemberQuota,
  type CompanyQuota, type MemberQuota,
} from '../../api/company-ai';
import { getNumberLocale } from '../../lib/base-i18n';

/**
 * 통합설정 › AI 연결 › **토큰 한도** (판 M).
 *
 * 2026-09-10 사용자 지시: 「회사 관리자는 사원별 기본 토큰과 사원별 토큰을 관리해야 한다」.
 *
 * ## ★★★ 넘어도 막지 않는다
 *
 * 계량은 provider 가 토큰 수를 알려 줬을 때만 쌓인다(관문 G1). CLI 는 안 알려 주는 경우가
 * 있어 **막기로 하면 CLI 사용자만 사실상 무제한**이 되어 규칙이 거짓말이 된다.
 * 그래서 한도는 «넘었는지 보여 주는 선»이다 — 화면이 그 사실을 적는다.
 *
 * ## ★★★ 세 상태를 «명시»한다
 *
 *   기본을 따른다 / 이 사람만 무제한 / 이 값
 *
 * 숫자 칸 하나로 두면 「비웠다」가 무제한인지 기본인지 알 수 없다. 그래서 고르기로 받는다.
 */

const fmt = (n: number): string => n.toLocaleString(getNumberLocale());
const parse = (v: string): number | null => {
  const t = v.replace(/[,\s]/g, '').trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
};

export function CompanyAiQuota(): JSX.Element {
  const [data, setData] = useState<CompanyQuota | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [defaultInput, setDefaultInput] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await getCompanyQuota();
      setData(d);
      setDefaultInput(d.memberDefault ?? '');
    } catch {
      setData(null);
      setError('한도를 불러오지 못했습니다.');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const flash = (): void => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2000);
  };

  const saveDefault = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await setMemberDefault(parse(defaultInput));
      await load();
      flash();
    } catch {
      setError('저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const saveMember = async (
    m: MemberQuota, mode: 'default' | 'unlimited' | 'limit', tokens?: number,
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await setMemberQuota(m.userId, mode, tokens);
      await load();
      flash();
    } catch {
      setError('저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  if (data === null) {
    return (
      <section className="company-ai__quota">
        <h4 className="company-ai__ttl">토큰 한도</h4>
        <p className="company-ai__note">{error ?? '불러오는 중…'}</p>
      </section>
    );
  }

  const co = data.company;

  return (
    <section className="company-ai__quota">
      <h4 className="company-ai__ttl">토큰 한도</h4>
      <p className="company-ai__hint">
        {data.month} 기준입니다. ★ 한도를 넘어도 <b>대화가 막히지는 않습니다</b> — 계량은 provider 가
        토큰 수를 알려 줬을 때만 쌓이고, 명령줄(CLI) 연결은 안 알려 주는 경우가 있어 막으면
        그쪽만 예외가 되기 때문입니다.
      </p>

      {error !== null && (
        <p className="company-ai__note company-ai__note--warn">
          <AlertTriangle size={14} aria-hidden="true" /> {error}
        </p>
      )}
      {saved && (
        <p className="company-ai__note company-ai__note--ok"><Check size={14} aria-hidden="true" /> 저장했습니다.</p>
      )}

      {/* 회사 한도 — 시스템 관리자가 정한다. 여기서는 «보기»만 한다. */}
      <div className="company-ai__quotaco">
        <span>우리 회사 한도</span>
        {co.limit === null ? (
          <b>무제한</b>
        ) : (
          <b className={co.over ? 'is-over' : undefined}>
            {fmt(co.usedThisMonth)} / {fmt(Number(co.limit))}
            {co.over ? ' — 넘었습니다' : ` · ${fmt(co.remaining ?? 0)} 남음`}
          </b>
        )}
        <span className="company-ai__hint">시스템 관리자가 정합니다.</span>
      </div>

      {/* 사원 기본 — 회사 관리자가 정한다. */}
      <div className="company-ai__quotadef">
        <label>
          <span>사원 기본 한도</span>
          <input
            value={defaultInput}
            onChange={(e) => setDefaultInput(e.target.value)}
            placeholder="비우면 무제한"
            inputMode="numeric"
            aria-label="사원 기본 한도"
          />
        </label>
        <button type="button" className="company-ai__def" disabled={busy} onClick={() => { void saveDefault(); }}>
          저장
        </button>
        <span className="company-ai__hint">사원별로 따로 정하지 않은 사람에게 걸립니다.</span>
      </div>

      {/* 사원별 — 세 상태를 «고르기»로 명시한다. */}
      <ul className="company-ai__mlist">
        {data.members.map((m) => (
          <li key={m.userId} className="company-ai__mrow">
            <span className="company-ai__mnm">{m.name}</span>
            <span className={`company-ai__mused${m.over ? ' is-over' : ''}`}>
              {fmt(m.usedThisMonth)}
              {m.limit !== null && ` / ${fmt(Number(m.limit))}`}
              {m.limit === null && ' · 무제한'}
            </span>
            <span className="company-ai__msrc">
              {m.source === 'user' ? '개별' : m.source === 'company-default' ? '기본' : '미설정'}
            </span>
            <MemberEditor member={m} busy={busy} onSave={saveMember} />
          </li>
        ))}
      </ul>
      {data.members.length === 0 && <p className="company-ai__note">사원이 없습니다.</p>}
    </section>
  );
}

/** 한 사람의 한도 — 세 상태를 고르기로 받는다. */
function MemberEditor({
  member, busy, onSave,
}: {
  member: MemberQuota;
  busy: boolean;
  onSave: (m: MemberQuota, mode: 'default' | 'unlimited' | 'limit', tokens?: number) => Promise<void>;
}): JSX.Element {
  const [mode, setMode] = useState<'default' | 'unlimited' | 'limit'>(
    member.source === 'user' ? (member.limit === null ? 'unlimited' : 'limit') : 'default',
  );
  const [tokens, setTokens] = useState(member.source === 'user' && member.limit !== null ? member.limit : '');

  return (
    <span className="company-ai__medit">
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value as typeof mode)}
        aria-label={`${member.name} 한도 방식`}
        disabled={busy}
      >
        <option value="default">기본을 따름</option>
        <option value="limit">직접 지정</option>
        <option value="unlimited">무제한</option>
      </select>
      {mode === 'limit' && (
        <input
          value={tokens}
          onChange={(e) => setTokens(e.target.value)}
          placeholder="토큰"
          inputMode="numeric"
          aria-label={`${member.name} 한도 값`}
          disabled={busy}
        />
      )}
      <button
        type="button"
        className="company-ai__def"
        disabled={busy || (mode === 'limit' && parse(tokens) === null)}
        onClick={() => { void onSave(member, mode, mode === 'limit' ? (parse(tokens) ?? 0) : undefined); }}
      >
        적용
      </button>
    </span>
  );
}
